import { FC, Fragment, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
	AlertTriangle,
	ArrowRightLeft,
	Ban,
	DollarSign,
	History,
	Mail,
	Pin,
	Printer,
	Repeat,
	RotateCcw,
	Send,
	StickyNote,
	TicketCheck,
	X,
} from 'lucide-react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	useCancelTicket,
	useRefundTicket,
	useResendEmail,
	useTransferTicket,
	useUncancelTicket,
	useUndoCheckIn,
	useValidateTicket,
	useVoidExternalTicket,
} from 'features/PunchupPages/CustomFieldsResponse/hooks/useEventCustomFields';
import {
	usePinnedActions,
	ATTENDEE_PINNED_DEFAULTS,
} from 'features/PunchupPages/CustomFieldsResponse/hooks/usePinnedActions';
import { ResponsesData } from 'features/CustomFieldsResponses/types';
import { IdParamsType } from 'utils/types';
import { canUndoCheckIn } from 'utils/utils';
import { useVisibleViewport } from 'features/common/hooks/useVisibleViewport';
import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';
import { CancelModal } from './components/CancelModal';
import { HistoryModal } from './components/HistoryModal';
import { RefundModal } from './components/RefundModal';
import { ResendModal } from './components/ResendModal';
import { SwitchTicketDelegator } from 'features/CustomFieldsResponses/components/AttendeeActions/SwitchTicketDelegator';
import { TransferModal } from './components/TransferModal';
import { UncancelModal } from './components/UncancelModal';
import { VoidModal } from './components/VoidModal';
import { FanNotesModal } from './components/FanNotesModal';
import { useMutationTimeout } from './useMutationTimeout';
import { delegateRefundToHost } from './delegateRefundToHost';
import { isSamePlacement, positionRowMenu, RowMenuPlacement } from './positionRowMenu';
import { usePrintTickets } from 'features/common/hooks/usePrintTickets';
import {
	PENDING_UPGRADE_STATUS,
	isCanceledStatus,
	isImportedStatus,
	isSwitchableStatus,
} from '../PunchupResponsesTable/statusColors';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { postToast } from 'features/common/utils/postFlash';
import styles from './AttendeeActions.module.css';

interface AttendeeActionsProps {
	data: ResponsesData;
	isPaidEvent?: boolean;
	transactionTickets: Record<string, number[]>;
}

type ActionId =
	| 'print'
	| 'resend'
	| 'transfer'
	| 'switch'
	| 'validate'
	| 'undo-check-in'
	| 'history'
	| 'notes'
	| 'uncancel'
	| 'cancel'
	| 'void'
	| 'refund';

interface MenuAction {
	id: ActionId;
	label: string;
	icon: ReactNode;
	onSelect: () => void;
	visible: boolean;
	/** Pinnable actions can be surfaced as a quick icon button beside the ⋮ trigger (per-event, see usePinnedActions). */
	pinnable: boolean;
	destructive?: boolean;
	/** Disables both the menu row and the quick button (e.g. Print while a print is in flight). */
	disabled?: boolean;
}

export const AttendeeActions: FC<AttendeeActionsProps> = ({ data, isPaidEvent = true, transactionTickets }) => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const { isPinned, togglePin } = usePinnedActions(eventId, ATTENDEE_PINNED_DEFAULTS);
	const btnRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState<RowMenuPlacement>({ top: 0, left: 0 });
	// While the menu is open, track the parent's visible viewport band so the
	// placement clamp works inside the iframe (subscribed only while open).
	const viewport = useVisibleViewport(open);

	const [showResendModal, setShowResendModal] = useState(false);
	const [showTransferModal, setShowTransferModal] = useState(false);
	const [showHistoryModal, setShowHistoryModal] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showUncancelModal, setShowUncancelModal] = useState(false);
	const [showRefundModal, setShowRefundModal] = useState(false);
	const [refundReason, setRefundReason] = useState('');
	const [showVoidModal, setShowVoidModal] = useState(false);
	const [voidReason, setVoidReason] = useState('');
	const [delegatingSwitch, setDelegatingSwitch] = useState(false);
	const [showNotesModal, setShowNotesModal] = useState(false);
	const [showValidateModal, setShowValidateModal] = useState(false);
	const [showUndoCheckInModal, setShowUndoCheckInModal] = useState(false);

	const onResendSuccess = useCallback(() => setShowResendModal(false), []);
	const onTransferSuccess = useCallback(() => setShowTransferModal(false), []);
	const onCancelSuccess = useCallback(() => setShowCancelModal(false), []);
	const onUncancelSuccess = useCallback(() => setShowUncancelModal(false), []);
	const onRefundSuccess = useCallback(() => {
		setShowRefundModal(false);
		setRefundReason('');
	}, []);
	const onVoidSuccess = useCallback(() => {
		setShowVoidModal(false);
		setVoidReason('');
	}, []);
	const onValidateSuccess = useCallback(() => setShowValidateModal(false), []);
	const onUndoCheckInSuccess = useCallback(() => setShowUndoCheckInModal(false), []);

	const { resendEmail, isLoading: loadingResend } = useResendEmail({ onSuccess: onResendSuccess });
	const { transferTicket, isLoading: loadingTransfer } = useTransferTicket({ onSuccess: onTransferSuccess });
	const { cancelTicket, isLoading: loadingCancel } = useCancelTicket({ onSuccess: onCancelSuccess });
	const { uncancelTicket, isLoading: loadingUncancel } = useUncancelTicket({ onSuccess: onUncancelSuccess });
	const { refundTicket, isLoading: loadingRefund } = useRefundTicket({ onSuccess: onRefundSuccess });
	const { voidExternalTicket, isLoading: loadingVoid } = useVoidExternalTicket({ onSuccess: onVoidSuccess });
	const { validateTicket, isLoading: loadingValidate } = useValidateTicket({ onSuccess: onValidateSuccess });
	const { undoCheckIn, isLoading: loadingUndoCheckIn } = useUndoCheckIn({ onSuccess: onUndoCheckInSuccess });

	const isResendLoading = useMutationTimeout(loadingResend, 'The email send request timed out. Please try again.');
	const isTransferLoading = useMutationTimeout(loadingTransfer, 'The transfer request timed out. Please try again.');
	const isCancelLoading = useMutationTimeout(loadingCancel, 'The cancel request timed out. Please try again.');
	const isUncancelLoading = useMutationTimeout(loadingUncancel, 'The un-cancel request timed out. Please try again.');
	const isRefundLoading = useMutationTimeout(
		loadingRefund,
		'The refund request timed out. Please check your connection and try again.'
	);
	const isVoidLoading = useMutationTimeout(loadingVoid, 'The void request timed out. Please try again.');
	const isValidateLoading = useMutationTimeout(loadingValidate, 'The validation request timed out. Please try again.');
	const isUndoCheckInLoading = useMutationTimeout(loadingUndoCheckIn, 'The undo request timed out. Please try again.');

	useEffect(() => {
		if (!open) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			// Close only when the click lands outside BOTH the trigger and the menu,
			// so a click on a menu item is not pre-empted before its onClick fires.
			const insideTrigger = btnRef.current?.contains(target);
			const insideMenu = menuRef.current?.contains(target);
			if (!insideTrigger && !insideMenu) setOpen(false);
		};
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open]);

	// On open, move focus to the first menu item for keyboard operability.
	useEffect(() => {
		if (!open) return;
		const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
		firstItem?.focus();
	}, [open]);

	// Measure the portalled menu and place it before paint, again whenever the host band moves or the frame
	// itself resizes; the placement contract is in ./CLAUDE.md (Dropdown menu).
	const visibleTop = viewport?.visibleTop;
	const viewportHeight = viewport?.viewportHeight;
	useLayoutEffect(() => {
		const triggerEl = btnRef.current;
		const menuEl = menuRef.current;
		if (!open || !triggerEl || !menuEl) return;
		const place = () => {
			// Natural height even when a previous placement capped it (scrollHeight is the uncapped content).
			const chromeHeight = menuEl.offsetHeight - menuEl.clientHeight;
			const menuHeight = menuEl.scrollHeight + chromeHeight;
			const hasHostBand = visibleTop !== undefined && viewportHeight !== undefined;
			const band = hasHostBand
				? { top: visibleTop, bottom: visibleTop + viewportHeight }
				: { top: 0, bottom: window.innerHeight };
			const frame = { width: window.innerWidth, height: window.innerHeight };
			const trigger = triggerEl.getBoundingClientRect();
			const measure = () =>
				positionRowMenu({ trigger, menu: { width: menuEl.offsetWidth, height: menuHeight }, band, frame });
			// The height cap decides whether the dropdown scrolls, and a classic scrollbar widens it (an overlay one
			// takes no space), so the cap is applied before the width that fixes the left edge is measured. The
			// second pass keeps top and maxHeight (they depend only on the height); React commits the same value.
			const { maxHeight } = measure();
			menuEl.style.maxHeight = maxHeight === undefined ? '' : maxHeight + 'px';
			const next = measure();
			setMenuPos((prev) => (isSamePlacement(prev, next) ? prev : next));
		};
		place();
		window.addEventListener('resize', place);
		return () => window.removeEventListener('resize', place);
	}, [open, visibleTop, viewportHeight]);

	// Revoke the object URL + remove the detached print iframe on unmount — a per-row component would
	// otherwise leak one frame + URL per print.
	const { isPrinting, print } = usePrintTickets({
		onError: (reason) =>
			postToast(
				'error',
				reason === 'fetch'
					? t('attendee-actions-print-load-error', 'Could not load the ticket PDF. Please try again.')
					: t('attendee-actions-print-error', 'Could not open the print dialog. Please try again.')
			),
	});

	const handlePrintTicket = () => print(`ticket-${data.ticket_id}`, { ticketIds: [data.ticket_id] }, 'thermal');

	const isRefunded = data.status?.toLowerCase() === 'refunded';
	// `External Canceled` (an import canceled or voided) is canceled too: Un-Cancel, no Validate / Cancel.
	const isCanceled = isCanceledStatus(data.status ?? '');
	// An import is voided, never refunded (TIC-952) — the API refuses the refund, so the row offers Void.
	// `imported` also covers an import that was transferred or switched on, whose status no longer says
	// so; the status read keeps External rows right against an API that doesn't send the flag yet.
	const isImported = !!data.imported || isImportedStatus(data.status ?? '');
	const isSwitchedTo = data.status?.toLowerCase() === 'switched to';
	// Held by an unpaid pending upgrade: the API 409s every mutation (switch/transfer/cancel/refund/
	// validate) until the fan pays, the window expires, or the upgrade is cancelled from the Punchup fan
	// page — so those actions are hidden rather than offered-then-failed.
	const isPendingUpgrade = data.pending_upgrade === true || data.status === PENDING_UPGRADE_STATUS;
	// Only these statuses may enter the switch workflow (mirrors the API's switch gate) — offering
	// Switch on e.g. a transferred row just produces a guaranteed 422.
	const canSwitch = isSwitchableStatus(data.status ?? '') && !isPendingUpgrade;
	// "live" = a normal sellable ticket (not refunded / switched-away / canceled) → the validate + cancel
	// affordances apply. "fullMenu" keeps the canceled row's management actions (transfer/switch/notes/
	// refund/un-cancel) but drops the live-only ones. Refunded/switched-to rows get the minimal menu.
	const isLive = !isRefunded && !isSwitchedTo && !isCanceled && !isPendingUpgrade;
	const fullMenu = !isRefunded && !isSwitchedTo && !isPendingUpgrade;
	// Already scanned → the validate action re-validates (cautionary copy + glyph) rather than first-validates.
	const isRevalidate = data.is_validated;
	// Undo is admin/editor only: any other role gets a 401 the api client treats as an expired session.
	const canUndo = data.is_validated && canUndoCheckIn(localStorage.getItem('accessToken'));

	// Declarative action list — drives both the ⋮ dropdown (with a pin toggle per pinnable row) and the
	// pinned quick-access icon buttons beside the trigger. Order is the dropdown render order; the single
	// separator is drawn before the first destructive item (Cancel / Refund). Print is a normal pinnable
	// row — pinned BY DEFAULT (ATTENDEE_PINNED_DEFAULTS), so it shows as a quick button out of the box but
	// the manager can unpin it. Cancel / Un-Cancel are pinnable too (the manager may want either one click).
	const menuActions: MenuAction[] = [
		{
			id: 'print',
			label: t('attendee-actions-print-pdf', 'Print ticket'),
			icon: isPrinting ? <PunchupSpinner size={16} /> : <Printer size={16} />,
			onSelect: handlePrintTicket,
			visible: !isSwitchedTo && !isRefunded,
			pinnable: true,
			disabled: isPrinting,
		},
		{
			id: 'resend',
			label: t('attendee-actions-resend-ticket', 'Resend Order Email'),
			icon: <Mail size={16} />,
			onSelect: () => setShowResendModal(true),
			visible: !isSwitchedTo,
			pinnable: true,
		},
		{
			id: 'transfer',
			label: t('attendee-actions-transfer-ticket', 'Transfer Ticket'),
			icon: <Send size={16} />,
			onSelect: () => setShowTransferModal(true),
			visible: fullMenu,
			pinnable: true,
		},
		{
			id: 'switch',
			label: t('attendee-actions-switch-ticket', 'Switch Ticket'),
			icon: <ArrowRightLeft size={16} />,
			onSelect: () => setDelegatingSwitch(true),
			visible: fullMenu && canSwitch,
			pinnable: true,
		},
		{
			id: 'validate',
			label: isRevalidate
				? t('attendee-actions-revalidate-ticket', 'Re-validate ticket')
				: t('attendee-actions-validate-ticket', 'Validate ticket'),
			icon: <TicketCheck size={16} />,
			onSelect: () => setShowValidateModal(true),
			visible: isLive,
			pinnable: true,
		},
		{
			id: 'undo-check-in',
			label: t('attendee-actions-undo-check-in', 'Undo check-in'),
			icon: <RotateCcw size={16} />,
			onSelect: () => setShowUndoCheckInModal(true),
			visible: canUndo,
			pinnable: false,
		},
		{
			id: 'history',
			label: t('attendee-actions-history', 'View History'),
			icon: <History size={16} />,
			onSelect: () => setShowHistoryModal(true),
			visible: true,
			pinnable: true,
		},
		{
			id: 'notes',
			label: t('attendee-actions-fan-notes', 'Fan Notes'),
			icon: <StickyNote size={16} />,
			onSelect: () => setShowNotesModal(true),
			visible: fullMenu,
			pinnable: true,
		},
		{
			id: 'uncancel',
			label: t('attendee-actions-uncancel-ticket', 'Un-Cancel Ticket'),
			icon: <Repeat size={16} />,
			onSelect: () => setShowUncancelModal(true),
			visible: fullMenu && isCanceled,
			pinnable: true,
		},
		{
			id: 'cancel',
			label: t('attendee-actions-cancel-ticket', 'Cancel Ticket'),
			icon: <X size={16} />,
			onSelect: () => setShowCancelModal(true),
			visible: fullMenu && !isCanceled,
			pinnable: true,
			destructive: true,
		},
		// The import's stand-in for Refund: Punchup never held its money. A canceled import is already voided.
		{
			id: 'void',
			label: t('attendee-actions-void-ticket', 'Void Ticket'),
			icon: <Ban size={16} />,
			onSelect: () => setShowVoidModal(true),
			visible: fullMenu && isImported && !isCanceled,
			pinnable: true,
			destructive: true,
		},
		// why: free/registration events have no payable amount, so a $0.00 refund is incoherent.
		{
			id: 'refund',
			label: t('attendee-actions-refund-ticket', 'Refund Ticket'),
			icon: <DollarSign size={16} />,
			onSelect: () => {
				if (!delegateRefundToHost(data, eventId)) setShowRefundModal(true);
			},
			visible: fullMenu && isPaidEvent && !isImported,
			pinnable: true,
			destructive: true,
		},
	];

	const visibleActions = menuActions.filter((a) => a.visible);
	const pinnedQuick = visibleActions.filter((a) => a.pinnable && isPinned(a.id));

	const renderPinToggle = (a: MenuAction) => {
		const pinned = isPinned(a.id);
		return (
			<button
				type="button"
				className={pinned ? styles.pinToggleActive : styles.pinToggle}
				// onClick (not onMouseDown) so the toggle is keyboard-operable (Enter/Space) as well as
				// pointer — it advertises aria-pressed, so it must be actuable by keyboard. stopPropagation
				// keeps the click off the row; the toggle is a sibling of the menu-item button (not nested),
				// and the outside-click close is mousedown-gated on `menuRef` (which contains this button), so
				// the menu stays open either way.
				onClick={(e) => {
					e.stopPropagation();
					togglePin(a.id);
				}}
				aria-pressed={pinned}
				aria-label={pinned ? t('attendee-actions-unpin', 'Unpin action') : t('attendee-actions-pin', 'Pin action')}
				title={
					pinned
						? t('attendee-actions-unpin-title', 'Unpin from quick access')
						: t('attendee-actions-pin-title', 'Pin to quick access')
				}
			>
				<Pin size={14} fill={pinned ? 'currentColor' : 'none'} />
			</button>
		);
	};

	const menu = (
		<div
			ref={menuRef}
			role="menu"
			className={styles.dropdown}
			style={{ top: menuPos.top, left: menuPos.left, maxHeight: menuPos.maxHeight }}
		>
			{visibleActions.map((a, idx) => {
				// One separator, drawn before the first destructive item (Cancel / Refund group).
				const showSeparator = !!a.destructive && idx > 0 && !visibleActions[idx - 1].destructive;
				return (
					<Fragment key={a.id}>
						{showSeparator && <div className={styles.separator} />}
						<div className={styles.itemRow}>
							<button
								type="button"
								role="menuitem"
								className={a.destructive ? styles.itemDestructive : styles.item}
								disabled={a.disabled}
								onClick={() => {
									a.onSelect();
									setOpen(false);
								}}
							>
								{a.icon}
								{a.label}
							</button>
							{a.pinnable && renderPinToggle(a)}
						</div>
					</Fragment>
				);
			})}
		</div>
	);

	return (
		<div className={styles.container}>
			<div className={styles.trigger}>
				{/* Pinned quick-access actions (per-event, see usePinnedActions) surface as one-click icon
				    buttons left of the ⋮ — visible only on rows where the action itself applies. Print is
				    pinned by default (ATTENDEE_PINNED_DEFAULTS), so it appears here out of the box; it keeps
				    its in-flight spinner via the action's `icon`, and is disabled while a print is running. */}
				{pinnedQuick.map((a) => (
					<button
						key={a.id}
						type="button"
						className={styles.quickBtn}
						onClick={() => a.onSelect()}
						disabled={a.disabled}
						aria-label={a.label}
						title={a.label}
					>
						{a.icon}
					</button>
				))}
				<button
					ref={btnRef}
					type="button"
					className={styles.menuBtn}
					onClick={() => setOpen((v) => !v)}
					aria-label={t('actions', 'Actions')}
					data-actions-trigger
				>
					<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
						<circle cx="12" cy="5" r="1.5" />
						<circle cx="12" cy="12" r="1.5" />
						<circle cx="12" cy="19" r="1.5" />
					</svg>
				</button>
			</div>

			{open && ReactDOM.createPortal(menu, document.body)}

			{showResendModal &&
				ReactDOM.createPortal(
					<ResendModal
						onConfirm={() => resendEmail(data.order_id)}
						onClose={() => setShowResendModal(false)}
						isLoading={isResendLoading}
					/>,
					document.body
				)}
			{showTransferModal &&
				ReactDOM.createPortal(
					<TransferModal
						ticketId={data.ticket_id}
						ticketTypesString={data.ticket_types_string}
						currentOwnerEmail={data.email}
						onConfirm={({ email, firstName, lastName }) =>
							transferTicket({
								ticketId: data.ticket_id,
								recipientEmail: email,
								recipientFirstName: firstName || undefined,
								recipientLastName: lastName || undefined,
							})
						}
						onClose={() => setShowTransferModal(false)}
						isLoading={isTransferLoading}
					/>,
					document.body
				)}
			{showHistoryModal &&
				ReactDOM.createPortal(
					<HistoryModal ticketId={data.ticket_id} onClose={() => setShowHistoryModal(false)} />,
					document.body
				)}
			{showCancelModal &&
				!isCanceled &&
				ReactDOM.createPortal(
					<CancelModal
						onConfirm={() => cancelTicket(data.ticket_id)}
						onClose={() => setShowCancelModal(false)}
						isLoading={isCancelLoading}
					/>,
					document.body
				)}
			{showUncancelModal &&
				isCanceled &&
				ReactDOM.createPortal(
					<UncancelModal
						onConfirm={() => uncancelTicket(data.ticket_id)}
						onClose={() => setShowUncancelModal(false)}
						isLoading={isUncancelLoading}
					/>,
					document.body
				)}
			{showVoidModal &&
				ReactDOM.createPortal(
					<VoidModal
						reason={voidReason}
						onReasonChange={setVoidReason}
						onConfirm={() => voidExternalTicket({ ticketId: data.ticket_id, reason: voidReason.trim() })}
						onClose={() => setShowVoidModal(false)}
						isLoading={isVoidLoading}
					/>,
					document.body
				)}
			{showRefundModal &&
				isPaidEvent &&
				ReactDOM.createPortal(
					<RefundModal
						amount={data.amount}
						reason={refundReason}
						onReasonChange={setRefundReason}
						onConfirm={() => refundTicket({ ticketId: data.ticket_id, amount: data.amount, description: refundReason })}
						onClose={() => setShowRefundModal(false)}
						isLoading={isRefundLoading}
					/>,
					document.body
				)}
			{delegatingSwitch && (
				<SwitchTicketDelegator
					data={data}
					onDone={() => setDelegatingSwitch(false)}
					transactionTickets={transactionTickets}
				/>
			)}
			{showNotesModal &&
				ReactDOM.createPortal(
					<FanNotesModal
						userId={data.user_id}
						email={data.email}
						fullName={data.full_name || ''}
						onClose={() => setShowNotesModal(false)}
					/>,
					document.body
				)}
			{showValidateModal &&
				ReactDOM.createPortal(
					// PunchupConfirmDialog has no amber "warning" variant — a re-validate uses the AlertTriangle
					// glyph (cautionary) with the default orange circle; a first validate uses the TicketCheck glyph
					// (matching the menu/quick-button action icon, per the legacy/Classic view — TIC-303).
					<PunchupConfirmDialog
						icon={isRevalidate ? <AlertTriangle size={24} /> : <TicketCheck size={24} />}
						title={
							isRevalidate
								? t('attendee-actions-revalidate-title', 'Re-validate ticket')
								: t('attendee-actions-validate-title', 'Validate ticket')
						}
						text={
							isRevalidate
								? t(
										'attendee-actions-revalidate-confirm',
										'This ticket has already been scanned. Re-validating records another check-in.'
								  )
								: t(
										'attendee-actions-validate-confirm',
										'This checks the attendee in and marks the ticket as scanned. Continue?'
								  )
						}
						isLoading={isValidateLoading}
						buttons={[
							{ label: t('button-cancel', 'Cancel'), variant: 'secondary', onClick: () => setShowValidateModal(false) },
							{
								label: t('button-confirm', 'Confirm'),
								variant: 'primary',
								onClick: () => eventId && validateTicket({ ticketId: data.ticket_id, eventId }),
							},
						]}
					/>,
					document.body
				)}
			{showUndoCheckInModal &&
				ReactDOM.createPortal(
					<PunchupConfirmDialog
						icon={<RotateCcw size={24} />}
						iconVariant="destructive"
						title={t('attendee-actions-undo-check-in-title', 'Undo this check-in?')}
						text={t(
							'attendee-actions-undo-check-in-confirm',
							'The ticket will show as not scanned for this show and can be scanned again.'
						)}
						isLoading={isUndoCheckInLoading}
						buttons={[
							{
								label: t('button-cancel', 'Cancel'),
								variant: 'secondary',
								onClick: () => setShowUndoCheckInModal(false),
							},
							{
								label: t('attendee-actions-undo-check-in', 'Undo check-in'),
								variant: 'destructive',
								icon: <RotateCcw size={16} />,
								onClick: () => eventId && undoCheckIn({ ticketId: data.ticket_id, eventId }),
							},
						]}
					/>,
					document.body
				)}
		</div>
	);
};
